import { JSX, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import * as vp from 'vopay';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Bowser from 'bowser';
import { clearMfaHash, getRememberMfa, toBool } from 'vopay';

import usePostRailzSync from '../hooks/useApi/usePostRailzSync';
import useFetchRailzBusinessAccount from '../hooks/useApi/useFetchRailzBusinessAccount';
import usePostPortalClientLogin from '../hooks/useApi/usePostPortalClientLogin';
import usePostClientAuthPing from '../hooks/useApi/usePostClientAuthPing';
import useStorage from '../hooks/useStorage';
import useFetchAccountPermissions from '../hooks/useApi/useFetchAccountPermissions';
import { useNavMenu } from './NavMenu.provider';
import { usePermissions } from './Permissions.provider';
import { useClientData } from './ClientData.provider';
import { useDataDog } from './Datadog.provider';
import { useCache } from './Cache.provider';
import { useApprovalWorkflow } from './ApprovalWorkflow.provider';
import useFetchGeolocation from '../hooks/useApi/useFetchGeolocation';
import usePostPortalClientLoginHistory from '../hooks/useApi/usePostPortalClientLoginHistory';

import type { PortalClientLoginRequest, PortalClientLoginJWTRequest } from '../hooks/useApi/usePostPortalClientLogin';

type ClientLoginCtx = {
  isAuthenticated: boolean | null;
  isSubmitting: boolean;
  isLogout: boolean;
  ErrorMessage: string;
  response: {
    Success: boolean | undefined;
    LoginUserName: string;
    AccountID: string;
    AccountName: string;
    JWT: string;
    FirebaseToken: string;
    Country: string;
    GCMEnabled: string;
    EFTCollectEnabled: string;
    EFTSendEnabled: string;
    ACHFundEnabled: string;
    ACHWithdrawEnabled: string;
    USDEFTCollectEnabled: string;
    USDEFTSendEnabled: string;
    RTRWithdrawEnabled: string;
    RTPWithdrawEnabled: string;
    FedwireWithdrawEnabled: string;
    FedwireFundEnabled: string;
    AFTEnabled: boolean;
    InteracMoneyRequestEnabled: string;
    InboundEtransferEnabled: string;
    InteracBulkPayoutEnabled: string;
    PortalViewOnly: string;
    MoneyRequestGenerateUrlEnabled: string;
    SubAccountPermissions: string;
    ClientAccountsEnabled: string;
    clientAccountsEnabled: boolean;
    AutoBalanceTransferEnabled: string;
    TransactionLabelOverrideEnabled: string;
    IQ11StandardEnabled: string;
    IQ11BoostEnabled: string;
    IQ11PlusEnabled: string;
    IQ11AdvantageEnabled: string;
    TransactionServiceDefault: string;
    PayLinkEnabled: string;
    CustomFlinksCredentialsSet: boolean | undefined;
    CustomPlaidCredentialsSet: boolean | undefined;
    CustomInveriteCredentialsSet: boolean | undefined;
    EnablePaylinkDecline: string;
    CreditCardEnabled: string;
    GooglePayEnabled: boolean;
    UserManagementEnabled: string;
    AccountingSoftwareIntegrationEnabled: boolean;
    AccountingSystemIntegrationEnabled: string;
    AccountTransferEnabled: string;
    PushToCardEnabled: string;
    RealTimeMoneyRequestEnabled: string;
    TransactionApprovalWorkflowEnabled: string;
    CustomGlobalPaySharedSecret: boolean | undefined;
    KYCLightEnabled: string;
    AccountType: vp.ClientPortalAccountType;

    Domain: string;
    MFAEnabled: boolean | undefined;
    PasswordResetRequired: boolean | undefined;
    AdditionalAccounts: { [key: string]: string } | undefined;
    BrandingEmailEnabled: string;
    BrandingELinxEnabled: string;
    BrandingPortalEnabled: string;
    BrandingOnboardingEnabled: string;
    EmailDomainEnabled: string;
    ChatSupportEnabled: boolean;
    ParentAccountID: string;
    ParentAccountName: string;
    SubAccountType: string;
    InterestAccountEnabled: string;
    InteracVerificationEnabled: boolean;
    BillPayEnabled: boolean;
    AccountAndBillingEnabled: boolean;
    FundMyAccountEnabled: boolean;
    WithdrawMyAccountEnabled: boolean;
    BankAccountTransferEnabled: boolean;
    OperationalAccountsEnabled: boolean;
    PartnerBillsAccount: boolean;
    PaymentsEnabled: boolean;
    CollectionsEnabled: boolean;
    ShowSubAccountOrgChart: boolean;
    PushToCardProcessor: vp.PushToCardProcessor | '';
    CreditCardProcessor: vp.CreditCardProcessor | '';
    ACHFundingProcessor: vp.ACHFundingProcessor | '';
    ACHWithdrawProcessor: vp.ACHWithdrawProcessor | '';
    InteracMoneyRequestProcessor: vp.InteracMoneyRequestProcessor | '';
    InteracBulkPayoutProcessor: vp.InteracBulkPayoutProcessor | '';
    FedwireProcessor: vp.FedwireProcessor | '';
    RTPProcessor: vp.RTPProcessor | '';
    FednowProcessor: vp.FednowProcessor | '';
    InteracBasicVerificationEnabled: string;
    InteracEnhancedVerificationEnabled: string;
    MicroDepositBankAccountVerificationEnabled: boolean;
    CardBasicVerificationEnabled: boolean;
    CardEnhancedVerificationEnabled: boolean;
    FileConversionEnabled: boolean;
    FileSubmissionEnabled: boolean;
    SupportTicketEnabled: boolean;
    DigitalWalletsEnabled: boolean;
    PaypalWithdrawEnabled: boolean;
    VenmoWithdrawEnabled: boolean;
  };
  storeLogin: () => void;
  login: (requestParams: PortalClientLoginRequest) => void;
  logout: () => void;
  setMessageLogout: (value: boolean) => void;
  switchUser: (requestParams: PortalClientLoginJWTRequest) => void;
  handleLoginUsername: (username: string) => void;
  isSuspiciousLogin: boolean;
};

export const ClientLoginContext = createContext<ClientLoginCtx>({
  isAuthenticated: false,
  isSubmitting: false,
  isLogout: true,
  ErrorMessage: '',
  response: {
    Success: false,
    LoginUserName: '',
    AccountID: '',
    AccountName: '',
    JWT: '',
    FirebaseToken: '',
    Country: '',
    GCMEnabled: '',
    EFTCollectEnabled: '',
    EFTSendEnabled: '',
    ACHFundEnabled: '',
    ACHWithdrawEnabled: '',
    USDEFTCollectEnabled: '',
    USDEFTSendEnabled: '',
    RTRWithdrawEnabled: '',
    RTPWithdrawEnabled: '',
    FedwireWithdrawEnabled: '',
    FedwireFundEnabled: '',
    AFTEnabled: false,
    InteracMoneyRequestEnabled: '',
    InboundEtransferEnabled: '',
    InteracBulkPayoutEnabled: '',
    PortalViewOnly: '',
    MoneyRequestGenerateUrlEnabled: '',
    KYCLightEnabled: '',
    SubAccountPermissions: '',
    ClientAccountsEnabled: '',
    clientAccountsEnabled: false,
    AutoBalanceTransferEnabled: '',
    TransactionLabelOverrideEnabled: '',
    IQ11StandardEnabled: '',
    IQ11BoostEnabled: '',
    IQ11PlusEnabled: '',
    IQ11AdvantageEnabled: '',
    TransactionServiceDefault: '',
    PayLinkEnabled: '',
    CustomFlinksCredentialsSet: false,
    CustomPlaidCredentialsSet: false,
    CustomInveriteCredentialsSet: false,
    EnablePaylinkDecline: '',
    CreditCardEnabled: '',
    GooglePayEnabled: false,
    UserManagementEnabled: '',
    AccountingSoftwareIntegrationEnabled: false,
    AccountingSystemIntegrationEnabled: '',
    AccountTransferEnabled: '',
    PushToCardEnabled: '',
    RealTimeMoneyRequestEnabled: '',
    CustomGlobalPaySharedSecret: false,
    TransactionApprovalWorkflowEnabled: '',
    EmailDomainEnabled: '0',
    ChatSupportEnabled: false,
    AccountType: 'Standard',
    Domain: '',
    MFAEnabled: false,
    PasswordResetRequired: false,
    AdditionalAccounts: {},
    BrandingEmailEnabled: '',
    BrandingELinxEnabled: '',
    BrandingPortalEnabled: '',
    BrandingOnboardingEnabled: '',
    ParentAccountID: '',
    ParentAccountName: '',
    SubAccountType: '',
    InterestAccountEnabled: '0',
    InteracVerificationEnabled: false,
    BillPayEnabled: false,
    AccountAndBillingEnabled: false,
    FundMyAccountEnabled: false,
    WithdrawMyAccountEnabled: false,
    BankAccountTransferEnabled: false,
    OperationalAccountsEnabled: false,
    PartnerBillsAccount: false,
    PaymentsEnabled: false,
    CollectionsEnabled: false,
    ShowSubAccountOrgChart: false,
    PushToCardProcessor: '',
    CreditCardProcessor: '',
    ACHFundingProcessor: '',
    ACHWithdrawProcessor: '',
    InteracMoneyRequestProcessor: '',
    InteracBulkPayoutProcessor: '',
    FedwireProcessor: '',
    RTPProcessor: '',
    FednowProcessor: '',
    InteracBasicVerificationEnabled: '0',
    InteracEnhancedVerificationEnabled: '0',
    SupportTicketEnabled: false,
    DigitalWalletsEnabled: false,
    MicroDepositBankAccountVerificationEnabled: false,
    CardBasicVerificationEnabled: false,
    CardEnhancedVerificationEnabled: false,
    FileConversionEnabled: false,
    FileSubmissionEnabled: false,
    PaypalWithdrawEnabled: false,
    VenmoWithdrawEnabled: false,
  },

  login: () => {},
  logout: () => {},
  setMessageLogout: () => {},
  storeLogin: () => {},
  switchUser: () => {},
  handleLoginUsername: () => {},
  isSuspiciousLogin: false,
});

export const ClientLoginProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isSuspiciousLogin, setIsSuspiciousLogin] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState('');
  const [loginResponse, refreshClientLogin, resetLogin] = usePostPortalClientLogin({});
  const [tokenValidationResponse, validateTokens] = usePostClientAuthPing({});
  const [loginUsernameRequest, setLoginUsername] = useState('');
  const [isSwitchUser, setSwitchUser] = useState(false);
  const history = useHistory();
  const { reset: resetNavMenu } = useNavMenu();
  const { setPermissions, reset: resetPermissions } = usePermissions();
  const { clearCache } = useCache();
  const { response, setClientData, reset: clearClientData } = useClientData();
  const { setUser, clearUser } = useDataDog();
  const [permissionsResponse, fetchPermissionsResponse, resetPermissionsResponse] = useFetchAccountPermissions({});
  const [logoutMessage, setMessageLogout] = useState(false);
  const [, , , fetchRailzBusinessAccount] = useFetchRailzBusinessAccount({});
  const [, railzSync] = usePostRailzSync({});
  const { usersReset, approvalListReset, transactionsReset } = useApprovalWorkflow();
  const {
    login: { getClientLogin, storeClientLogin, clearClientLogin, storeLoginValues, storeLoginUsername },
    permissions: { getClientPermissions, storePermissions, clearPermissions },
    banner: { clearBanner },
    NoOperationalDefault: { clearNoOperationalDefaultModal },
  } = useStorage();
  const [PortalClientLoginHistoryResponse, postPortalClientLoginHistory] = usePostPortalClientLoginHistory({});
  const [geolocationResponse, fetchGeolocation] = useFetchGeolocation({});

  const {
    Success,
    LoginUserName,
    AccountID,
    AccountName,
    Key,
    Signature,
    JWT,
    FirebaseToken,
    Country,
    GCMEnabled,
    EFTCollectEnabled,
    EFTSendEnabled,
    ACHFundEnabled,
    ACHWithdrawEnabled,
    USDEFTCollectEnabled,
    USDEFTSendEnabled,
    RTRWithdrawEnabled,
    RTPWithdrawEnabled,
    FedwireWithdrawEnabled,
    FedwireFundEnabled,
    AFTEnabled,
    InteracMoneyRequestEnabled,
    InboundEtransferEnabled,
    InteracBulkPayoutEnabled,
    PortalViewOnly,
    MoneyRequestGenerateUrlEnabled,
    SubAccountPermissions,
    ClientAccountsEnabled,
    AutoBalanceTransferEnabled,
    TransactionLabelOverrideEnabled,
    IQ11StandardEnabled,
    IQ11BoostEnabled,
    IQ11PlusEnabled,
    IQ11AdvantageEnabled,
    TransactionServiceDefault,
    PayLinkEnabled,
    CustomFlinksCredentialsSet,
    CustomPlaidCredentialsSet,
    CustomInveriteCredentialsSet,
    EnablePaylinkDecline,
    CreditCardEnabled,
    GooglePayEnabled,
    UserManagementEnabled,
    AccountingSoftwareIntegrationEnabled,
    AccountingSystemIntegrationEnabled,
    AccountTransferEnabled,
    PushToCardEnabled,
    RealTimeMoneyRequestEnabled,
    CustomGlobalPaySharedSecret,
    KYCLightEnabled,
    AccountType,
    Domain,
    MFAEnabled,
    PasswordResetRequired,
    AdditionalAccounts,
    BrandingEmailEnabled,
    BrandingELinxEnabled,
    BrandingPortalEnabled,
    BrandingOnboardingEnabled,
    TransactionApprovalWorkflowEnabled,
    EmailDomainEnabled,
    ChatSupportEnabled,
    ParentAccountID,
    ParentAccountName,
    SubAccountType,
    InterestAccountEnabled,
    InteracVerificationEnabled,
    BillPayEnabled,
    AccountAndBillingEnabled,
    FundMyAccountEnabled,
    WithdrawMyAccountEnabled,
    BankAccountTransferEnabled,
    OperationalAccountsEnabled,
    PartnerBillsAccount,
    PaymentsEnabled,
    CollectionsEnabled,
    ShowSubAccountOrgChart,
    PushToCardProcessor,
    CreditCardProcessor,
    ACHFundingProcessor,
    ACHWithdrawProcessor,
    InteracMoneyRequestProcessor,
    InteracBulkPayoutProcessor,
    FedwireProcessor,
    RTPProcessor,
    FednowProcessor,
    InteracBasicVerificationEnabled,
    MicroDepositBankAccountVerificationEnabled,
    CardBasicVerificationEnabled,
    CardEnhancedVerificationEnabled,
    FileConversionEnabled,
    FileSubmissionEnabled,
    InteracEnhancedVerificationEnabled,
    SupportTicketEnabled,
    DigitalWalletsEnabled,
    PaypalWithdrawEnabled,
    VenmoWithdrawEnabled,
  } = getClientLogin();

  const storedPermissions = getClientPermissions();

  useEffect(() => (storedPermissions ? setPermissions(storedPermissions) : resetPermissions()), [storedPermissions]);

  useEffect(() => {
    if (response.Success) {
      fetchPermissionsResponse({
        PortalUser: response.LoginUserName,
        EmailAddress: response.LoginUserName,
      });
    }
  }, [AccountID, Key, Signature, response]);

  useEffect(() => {
    const handlers = {
      tokens: () => validateTokens(),
      noTokens: () => {
        setIsAuthenticated(false);
        clearClientLogin();
        // history.push('/');
      },
    };

    return hasStoredLoginData() ? handlers.tokens() : handlers.noTokens();
  }, []);

  useEffect(() => {
    if (loginResponse?.Success === false) {
      setErrorMessage(loginResponse.ErrorMessage);
    }
  }, [loginResponse]);

  useEffect(() => {
    const handlers = {
      success: () => {
        setIsAuthenticated(true);
        fetchGeolocation();
      },
      fail: () => {
        clearUser();
        clearClientLogin();
        if (isAuthenticated === true) {
          setMessageLogout(true);
          history.push('/');
        }
        setIsAuthenticated(false);
      },
    };
    if (tokenValidationResponse) return tokenValidationResponse.success ? handlers.success() : handlers.fail();

    return () => {};
  }, [tokenValidationResponse]);

  useEffect(() => {
    if (geolocationResponse) {
      const browserInfo = Bowser.getParser(window.navigator.userAgent).getResult();

      const loginInfo: vp.PortalClientLoginHistoryRequest = {
        BrowserName: browserInfo.browser.name,
        BrowserVersion: browserInfo.browser.version,
        City: geolocationResponse.city,
        Country: geolocationResponse.country,
        BrowserEngineName: browserInfo.engine.name,
        BrowserEngineVersion: browserInfo.engine.version,
        Hostname: window.location.hostname,
        IPAddress: geolocationResponse.ip,
        Location: geolocationResponse.loc,
        Organization: geolocationResponse.org,
        OSName: browserInfo.os.name,
        OSVersion: browserInfo.os.version,
        OSVersionName: browserInfo.os.versionName,
        PlatformType: browserInfo.platform.type,
        PlatformVendor: browserInfo.platform.vendor,
        PostalCode: geolocationResponse.postal,
        Region: geolocationResponse.region,
        TimeZone: geolocationResponse.city,
        Username: LoginUserName,
        RefferingUrl: document.referrer,
        RawUserAgent: navigator.userAgent,
      };
      postPortalClientLoginHistory(loginInfo);
    }
  }, [geolocationResponse]);

  useEffect(() => {
    if (PortalClientLoginHistoryResponse.Success) {
      if (PortalClientLoginHistoryResponse.SuspiciousLogin) {
        setIsSuspiciousLogin(true);
      }
    }
  }, [PortalClientLoginHistoryResponse]);

  useEffect(() => {
    if (loginResponse) {
      const usernameDatadog = isSwitchUser ? LoginUserName : loginUsernameRequest;
      if (isSwitchUser) {
        storeClientLogin(loginResponse, LoginUserName || loginUsernameRequest);
        storeLoginValues(loginResponse);
        setSwitchUser(false);
        history.push('/dashboard');
      } else {
        storeClientLogin(loginResponse, loginUsernameRequest);
      }
      if (loginResponse.SubAccountType === 'franchise') {
        const userLang = navigator.language.split('-')[0];
        i18n.changeLanguage(userLang).then(() => {
          localStorage.setItem('i18nextLng', userLang); // Update the cache
        });
      } else {
        i18n.changeLanguage('en').then(() => {
          localStorage.setItem('i18nextLng', 'en'); // Update the cache
        });
      }
      setIsSubmitting(false);
      setUser({
        usernameDatadog,
        accountId: loginResponse.AccountID,
        key: loginResponse.Key,
        signature: loginResponse.Signature,
        JWT: loginResponse.JWT,
      });

      if (loginResponse.AccountingSoftwareIntegrationEnabled) {
        const fetchRailz = async () => {
          const railzBusinessAccount = await fetchRailzBusinessAccount({
            PortalUser: LoginUserName,
          });

          if (!railzBusinessAccount.Success) return;

          const accountHasBeenSetUp = Object.values(railzBusinessAccount.Accounts).length > 0;

          if (!accountHasBeenSetUp) return;

          if (accountHasBeenSetUp) {
            railzSync({
              BusinessName: railzBusinessAccount.Accounts[0].BusinessName,
              ServiceName: railzBusinessAccount.Accounts[0].ServiceName,
            });
          }
        };
        fetchRailz();
      }
    }
  }, [loginResponse]);

  useEffect(() => {
    storePermissions(permissionsResponse);
    if (permissionsResponse.Success) {
      usersReset();
      approvalListReset();
      transactionsReset();
    }
    if (!loginResponse) return;
    if (permissionsResponse.Success) {
      // Check for redirect parameter and navigate accordingly
      const urlParams = new URLSearchParams(window.location.search);
      const redirectPath = urlParams.get('redirect');

      if (redirectPath) {
        try {
          // Decode the redirect path and navigate to it
          const decodedPath = decodeURIComponent(redirectPath);
          history.push(decodedPath);
        } catch (error) {
          // If redirect path is invalid, navigate to dashboard
          console.warn('Invalid redirect path, navigating to dashboard', error);
          history.push('/dashboard');
        }
      } else {
        // Default behavior - navigate to dashboard
        history.push('/dashboard');
      }
    }
  }, [permissionsResponse, loginResponse]);

  useEffect(() => {
    setClientData({
      Success,
      LoginUserName,
      AccountID,
      AccountName,
      Key,
      Signature,
      JWT,
      FirebaseToken,
      Country,
      GCMEnabled,
      EFTCollectEnabled,
      EFTSendEnabled,
      ACHFundEnabled,
      ACHWithdrawEnabled,
      USDEFTCollectEnabled,
      USDEFTSendEnabled,
      RTRWithdrawEnabled,
      RTPWithdrawEnabled,
      FedwireWithdrawEnabled,
      FedwireFundEnabled,
      AFTEnabled,
      InteracMoneyRequestEnabled,
      InboundEtransferEnabled,
      InteracBulkPayoutEnabled,
      PortalViewOnly,
      MoneyRequestGenerateUrlEnabled,
      KYCLightEnabled,
      SubAccountPermissions,
      ClientAccountsEnabled,
      clientAccountsEnabled: vp.toBool(ClientAccountsEnabled),
      AutoBalanceTransferEnabled,
      TransactionLabelOverrideEnabled,
      IQ11StandardEnabled,
      IQ11BoostEnabled,
      IQ11PlusEnabled,
      IQ11AdvantageEnabled,
      TransactionServiceDefault,
      PayLinkEnabled,
      CustomFlinksCredentialsSet,
      CustomPlaidCredentialsSet,
      CustomInveriteCredentialsSet,
      EnablePaylinkDecline,
      CreditCardEnabled,
      GooglePayEnabled,
      UserManagementEnabled,
      AccountingSoftwareIntegrationEnabled,
      AccountingSystemIntegrationEnabled,
      AccountTransferEnabled,
      RealTimeMoneyRequestEnabled,
      CustomGlobalPaySharedSecret,
      PushToCardEnabled,
      AccountType,
      Domain,
      MFAEnabled,
      PasswordResetRequired,
      BrandingEmailEnabled,
      BrandingELinxEnabled,
      BrandingPortalEnabled,
      BrandingOnboardingEnabled,
      TransactionApprovalWorkflowEnabled,
      EmailDomainEnabled,
      ChatSupportEnabled,
      ParentAccountID,
      ParentAccountName,
      SubAccountType,
      InterestAccountEnabled: toBool(InterestAccountEnabled),
      InteracVerificationEnabled,
      BillPayEnabled,
      AccountAndBillingEnabled,
      FundMyAccountEnabled,
      WithdrawMyAccountEnabled,
      BankAccountTransferEnabled,
      OperationalAccountsEnabled,
      PartnerBillsAccount,
      PaymentsEnabled,
      CollectionsEnabled,
      ShowSubAccountOrgChart,
      PushToCardProcessor,
      CreditCardProcessor,
      ACHFundingProcessor,
      ACHWithdrawProcessor,
      InteracMoneyRequestProcessor,
      InteracBulkPayoutProcessor,
      FedwireProcessor,
      RTPProcessor,
      FednowProcessor,
      InteracBasicVerificationEnabled: toBool(InteracBasicVerificationEnabled),
      InteracEnhancedVerificationEnabled: toBool(InteracEnhancedVerificationEnabled),
      MicroDepositBankAccountVerificationEnabled,
      CardBasicVerificationEnabled,
      CardEnhancedVerificationEnabled,
      FileConversionEnabled,
      SupportTicketEnabled,
      DigitalWalletsEnabled,
      FileSubmissionEnabled,
      PaypalWithdrawEnabled,
      VenmoWithdrawEnabled,
    });

    if (AccountID !== '') {
      setUser({
        loginUsernameRequest: LoginUserName,
        AccountID,
        key: Key,
        signature: Signature,
        JWT,
      });
    }
  }, [AccountID, Key, Signature, AccountName, LoginUserName]);

  useEffect(() => {
    if (loginResponse?.Success && hasStoredLoginData() && !logoutMessage) {
      setIsAuthenticated(true);
    }
  }, [JWT]);

  const handleLogin = (requestParams: PortalClientLoginRequest) => {
    clearClientLogin();
    resetPermissionsResponse();
    resetLogin();

    clearClientData();
    clearCache();
    clearBanner();
    clearPermissions();
    setIsAuthenticated(false);
    clearUser();
    setIsSubmitting(true);
    setLoginUsername(requestParams.Username);
    refreshClientLogin(requestParams);
    clearNoOperationalDefaultModal();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearClientLogin();
    clearPermissions();
    clearCache();
    resetNavMenu();
    clearClientData();
    clearUser();

    if (!getRememberMfa(LoginUserName)) {
      clearMfaHash(LoginUserName);
    }

    history.push('/');
  };
  const handleStoreClientLogin = () => {
    if (loginResponse && loginResponse?.Success) {
      storeLoginValues(loginResponse);
      fetchGeolocation();
    }
  };
  const handleLoginUsername = (username: string) => {
    storeLoginUsername(username);
    setLoginUsername(username);
  };
  const handleSwitchUser = (requestParams: PortalClientLoginJWTRequest) => {
    setIsSubmitting(true);
    setSwitchUser(true);
    refreshClientLogin(requestParams);
  };

  return (
    <ClientLoginContext.Provider
      value={{
        isAuthenticated,
        isSubmitting,
        isLogout: logoutMessage,
        ErrorMessage,
        response: {
          Success,
          LoginUserName,
          AccountID,
          JWT,
          AccountName,
          FirebaseToken,
          Country,
          GCMEnabled,
          EFTCollectEnabled,
          EFTSendEnabled,
          ACHFundEnabled,
          ACHWithdrawEnabled,
          USDEFTCollectEnabled,
          USDEFTSendEnabled,
          RTRWithdrawEnabled,
          RTPWithdrawEnabled,
          FedwireWithdrawEnabled,
          FedwireFundEnabled,
          AFTEnabled,
          InteracMoneyRequestEnabled,
          InboundEtransferEnabled,
          InteracBulkPayoutEnabled,
          PortalViewOnly,
          MoneyRequestGenerateUrlEnabled,
          KYCLightEnabled,
          SubAccountPermissions,
          ClientAccountsEnabled,
          clientAccountsEnabled: vp.toBool(ClientAccountsEnabled),
          AutoBalanceTransferEnabled,
          TransactionLabelOverrideEnabled,
          IQ11StandardEnabled,
          IQ11BoostEnabled,
          IQ11PlusEnabled,
          IQ11AdvantageEnabled,
          TransactionServiceDefault,
          PayLinkEnabled,
          CustomFlinksCredentialsSet,
          CustomPlaidCredentialsSet,
          CustomInveriteCredentialsSet,
          EnablePaylinkDecline,
          CreditCardEnabled,
          GooglePayEnabled,
          UserManagementEnabled,
          AccountingSoftwareIntegrationEnabled,
          AccountingSystemIntegrationEnabled,
          AccountTransferEnabled,
          PushToCardEnabled,
          RealTimeMoneyRequestEnabled,
          CustomGlobalPaySharedSecret,
          AccountType,
          Domain,
          MFAEnabled,
          PasswordResetRequired,
          AdditionalAccounts,
          BrandingEmailEnabled,
          BrandingELinxEnabled,
          BrandingPortalEnabled,
          BrandingOnboardingEnabled,
          TransactionApprovalWorkflowEnabled,
          EmailDomainEnabled,
          ChatSupportEnabled,
          ParentAccountID,
          ParentAccountName,
          SubAccountType,
          InterestAccountEnabled,
          MicroDepositBankAccountVerificationEnabled,
          InteracVerificationEnabled,
          BillPayEnabled,
          AccountAndBillingEnabled,
          FundMyAccountEnabled,
          WithdrawMyAccountEnabled,
          BankAccountTransferEnabled,
          OperationalAccountsEnabled,
          PartnerBillsAccount,
          PaymentsEnabled,
          CollectionsEnabled,
          ShowSubAccountOrgChart,
          PushToCardProcessor,
          CreditCardProcessor,
          ACHFundingProcessor,
          ACHWithdrawProcessor,
          InteracMoneyRequestProcessor,
          InteracBulkPayoutProcessor,
          FedwireProcessor,
          RTPProcessor,
          FednowProcessor,
          InteracBasicVerificationEnabled,
          CardBasicVerificationEnabled,
          CardEnhancedVerificationEnabled,
          FileConversionEnabled,
          FileSubmissionEnabled,
          InteracEnhancedVerificationEnabled,
          SupportTicketEnabled,
          DigitalWalletsEnabled,
          PaypalWithdrawEnabled,
          VenmoWithdrawEnabled,
        },
        login: handleLogin,
        logout: handleLogout,
        setMessageLogout,
        storeLogin: handleStoreClientLogin,
        handleLoginUsername,
        switchUser: handleSwitchUser,

        isSuspiciousLogin,
      }}
    >
      {children}
    </ClientLoginContext.Provider>
  );

  function hasStoredLoginData() {
    return Boolean(JWT);
  }
};

export const useClientLogin = (): ClientLoginCtx => useContext(ClientLoginContext);
